import { Badge, Button } from '@/components/atoms';
import { formatCurrency } from '@/lib/helpers/format-currency';
import { parsePlanFeature } from '@/lib/helpers/subscription-plan';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';

interface PricingCardProps {
  isRecommended?: boolean;
  isPopular?: boolean;
  isCurrent?: boolean;
  name: string;
  description: string;
  amount: number;
  features: string[];
  intervalLabel?: string;
  platformFeePercent?: number;
  currencyCode?: string;
  planId?: number;
  ctaLabel?: string;
  onSelect?: (planId: number) => void;
  isLoading?: boolean;
  className?: string;
}

export const PricingCard = ({
  isRecommended = false,
  isPopular = false,
  isCurrent = false,
  name,
  description,
  amount,
  features,
  intervalLabel = 'month',
  platformFeePercent = amount > 0 ? 0 : 2,
  currencyCode = 'NGN',
  planId,
  ctaLabel,
  onSelect,
  isLoading,
  className,
}: PricingCardProps) => {
  const isFree = amount <= 0;
  const priceLabel = isFree
    ? 'Free'
    : formatCurrency(amount, { currency: currencyCode });
  const feeLabel =
    platformFeePercent === 0
      ? '0% platform fee'
      : `${platformFeePercent}% platform fee on sales`;

  const buttonLabel =
    ctaLabel ??
    (isCurrent
      ? 'Current plan'
      : isFree
        ? 'Free forever'
        : `Upgrade to ${name}`);

  return (
    <div
      className={cn(
        'relative flex h-full flex-col rounded-2xl border bg-white p-6 shadow-sm',
        isRecommended && 'border-primary ring-1 ring-primary/20',
        className
      )}
    >
      {(isRecommended || isPopular) && (
        <div className="absolute -top-3 left-6">
          <Badge className="bg-primary text-primary-foreground">
            {isRecommended ? 'Recommended' : 'Most popular'}
          </Badge>
        </div>
      )}

      <div className="flex flex-1 flex-col gap-5">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-2xl font-semibold tracking-tight text-foreground">
              {name}
            </h3>
            {isFree && (
              <Badge
                variant="outline"
                className="border-emerald-200 bg-emerald-50 text-emerald-700"
              >
                Free forever
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>

        <div className="space-y-3">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-semibold tracking-tight">
              {priceLabel}
            </span>
            {!isFree && (
              <span className="text-sm text-muted-foreground">
                /{intervalLabel}
              </span>
            )}
          </div>
          <span
            className={cn(
              'inline-flex rounded-full px-2.5 py-1 text-xs font-medium',
              platformFeePercent === 0
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-amber-50 text-amber-800'
            )}
          >
            {feeLabel}
          </span>
        </div>

        <Button
          className="w-full"
          variant={isFree || isCurrent ? 'outline' : 'default'}
          onClick={() => planId && onSelect?.(planId)}
          disabled={!planId || isCurrent || isFree || isLoading}
          isLoading={isLoading}
        >
          {buttonLabel}
        </Button>

        <div className="border-t border-border pt-5">
          <ul className="space-y-3">
            {features.map((feature, index) => {
              const parsed = parsePlanFeature(feature);
              if (!parsed.label) return null;

              if (parsed.isGroupHeader) {
                return (
                  <li
                    key={`${parsed.label}-${index}`}
                    className="pt-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase"
                  >
                    {parsed.label.replace(/:$/, '')}
                  </li>
                );
              }

              return (
                <li
                  key={`${parsed.label}-${index}`}
                  className="flex items-start gap-2.5 text-sm text-foreground"
                >
                  <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                    <Check className="size-3" strokeWidth={3} />
                  </span>
                  <span className="flex flex-wrap items-center gap-2 leading-5">
                    {parsed.label}
                    {parsed.badge === 'new' && (
                      <Badge className="bg-sky-50 text-sky-700 hover:bg-sky-50">
                        New
                      </Badge>
                    )}
                    {parsed.badge === 'coming-soon' && (
                      <Badge className="bg-amber-50 text-amber-800 hover:bg-amber-50">
                        Coming soon
                      </Badge>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};
